import { SCRIPT_CONFIG } from 'constant/scriptLoader';
import { useEffect } from 'react';
import { useScript } from 'hooks/useScript';
import useUser from 'hooks/useUser';

export interface Analytics {
  initialize?: boolean;
  invoked?: boolean;
  methods?: string[];
  factory?: (method: string) => (...args: unknown[]) => Analytics;
  load?: (key: string, options: Record<string, unknown>) => void;
  push?: (args: unknown[]) => void;
  _writeKey?: string;
  SNIPPET_VERSION?: string;
  _loadOptions?: Record<string, unknown>;
  identify?: (userId: string) => void;
  page?: (...args: unknown[]) => void;
  track?: (...args: unknown[]) => void;
  [key: string]: unknown;
}

// Utility functions
const createScriptElement = (content: string): HTMLScriptElement => {
  const script = document.createElement('script');
  script.textContent = content;
  return script;
};

const injectScript = (content: string): void => {
  const script = createScriptElement(content);
  document.head.appendChild(script);
};

// Stonly Widget initialization
const initializeStonlyWidget = (): void => {
  const { widgetId } = SCRIPT_CONFIG.STONLY;

  try {
    (window as Window & { STONLY_WID?: string }).STONLY_WID = widgetId;
  } catch {
    // If assignment fails, continue
  }

  const initScript = `
    window.STONLY_WID = '${widgetId}';
    !(function (s, t, o, n, l, y, w, g, d, e) {
      s.StonlyWidget ||
        (((d = s.StonlyWidget =
          function () {
            d._api ? d._api.apply(d, arguments) : d.queue.push(arguments);
          }).scriptPath = n),
        (d.apiPath = l),
        (d.sPath = y),
        (d.queue = []),
        ((g = t.createElement(o)).async = !0),
        (e = new XMLHttpRequest()).open(
          'GET',
          n + 'version?v=' + Date.now(),
          !0,
        ),
        (e.onreadystatechange = function () {
          4 === e.readyState &&
            ((g.src =
              n +
              'stonly-widget.js?v=' +
              (200 === e.status ? e.responseText : Date.now())),
            (w = t.getElementsByTagName(o)[0]).parentNode.insertBefore(g, w));
        }),
        e.send());
    })(window, document, 'script', 'https://stonly.com/js/widget/v2/');
  `;

  injectScript(initScript);
};

// Pylon Widget initialization
const initializePylonWidget = (): void => {
  const initScript = `
    (function () {
      var e = window;
      var t = document;
      var n = function () {
        n.e(arguments);
      };
      n.q = [];
      n.e = function (e) {
        n.q.push(e);
      };
      e.Pylon = n;
      var r = function () {
        var e = t.createElement('script');
        e.setAttribute('type', 'text/javascript');
        e.setAttribute('async', 'true');
        e.setAttribute(
          'src',
          'https://widget.usepylon.com/widget/3ae2d970-2dbb-43f2-b6c6-dab95aaee6df',
        );
        var n = t.getElementsByTagName('script')[0];
        n.parentNode.insertBefore(e, n);
      };
      if (t.readyState === 'complete') {
        r();
      } else if (e.addEventListener) {
        e.addEventListener('load', r, false);
      }
    })();
  `;

  injectScript(initScript);
};

// Custom hook for Google Maps
// export const useGoogleMaps = () => {
//   const { user } = useUser();
//   const mapApiKey = CONFIG.MAP_API;
//   const status = useScript(
//     `https://maps.googleapis.com/maps/api/js?key=${mapApiKey}&region=AUS&libraries=places`,
//     {
//       removeOnUnmount: false,
//       id: SCRIPT_CONFIG.GOOGLE_MAPS.id,
//       shouldPreventLoad: !user || !!window.google?.maps,
//     },
//   );

//   return status;
// };

// Custom hook for Stonly Widget
export const useStonlyWidget = () => {
  const { user } = useUser();
  const status = useScript(SCRIPT_CONFIG.STONLY.url, {
    removeOnUnmount: false,
    id: SCRIPT_CONFIG.STONLY.id,
    shouldPreventLoad: !user,
  });

  useEffect(() => {
    if (status === 'ready' && !('StonlyWidget' in window)) {
      initializeStonlyWidget();
    }
  }, [status]);

  return status;
};

// Custom hook for Pylon Widget
export const usePylonWidget = () => {
  const { user } = useUser();
  const status = useScript(SCRIPT_CONFIG.PYLON.url, {
    removeOnUnmount: false,
    id: SCRIPT_CONFIG.PYLON.id,
    shouldPreventLoad: !user,
  });

  useEffect(() => {
    if (status === 'ready' && !('Pylon' in window)) {
      initializePylonWidget();
    }
  }, [status]);

  return status;
};

// export const useSegmentAnalytics = () => {
//   const status = useScript(SCRIPT_CONFIG.SEGMENT.url, {
//     removeOnUnmount: false,
//     id: SCRIPT_CONFIG.SEGMENT.id,
//   });

//   useEffect(() => {
//     if (status === 'ready' && !(window.analytics as Analytics)?.initialize) {
//       const analytics: Analytics = (window.analytics = (window.analytics ||
//         []) as unknown as Analytics);
//       initializeSegmentAnalytics(analytics);
//     }
//   }, [status]);

//   return status;
// };
